export interface AuthClientConfig {
  authApiUrl: string;
}

// SSO: /me returns identity only. Authorization (does this user have a
// license for app X?) is each app's responsibility — call its API and
// expect 403 with `{error: "no_license_for_app"}` if not.
//
// LINE link state lives on the session (not per-app), so /me surfaces it
// here as a hint to the UI. `lineDisplayName` is only present when the
// user has actually linked a LINE account.
export interface MeResponse {
  email: string;
  lineLinked: boolean;
  lineDisplayName?: string;
  linePictureUrl?: string;
  lineLinkedAt?: number;
}

export interface StartLoginInput {
  returnTo: string;
}

export interface StartLoginResult {
  authUrl: string;
}

export interface GenerateLineCodeInput {
  appId: string;
}

export interface GenerateLineCodeResult {
  code: string;
  expiresAt: number;
}

export interface UnlinkLineInput {
  appId: string;
}

export interface GetMeInput {
  // Pass the app ID so the server can restore LINE link state from the
  // email→LINE reverse index when the session is fresh (post-relogin).
  appId?: string;
}

export interface AuthClient {
  startLogin(input: StartLoginInput): Promise<StartLoginResult>;
  getMe(input?: GetMeInput): Promise<MeResponse | null>;
  logout(): Promise<void>;
  generateLineCode(input: GenerateLineCodeInput): Promise<GenerateLineCodeResult>;
  unlinkLine(input: UnlinkLineInput): Promise<void>;
}

export { refreshAuth, withRefreshOn401, type RefreshConfig } from "./refresh.js";

export function createAuthClient(config: AuthClientConfig): AuthClient {
  const base = config.authApiUrl.replace(/\/$/, "");

  return {
    async startLogin(input) {
      const res = await fetch(`${base}/oauth/start`, {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          return_to: input.returnTo,
        }),
      });
      if (!res.ok) {
        throw new Error(`oauth start failed: ${res.status}`);
      }
      const data = (await res.json()) as { auth_url: string };
      return { authUrl: data.auth_url };
    },

    async getMe(input) {
      const headers: Record<string, string> = {};
      if (input?.appId) headers["X-App-Id"] = input.appId;
      const res = await fetch(`${base}/me`, {
        credentials: "include",
        cache: "no-store",
        headers,
      });
      if (res.status === 401) return null;
      if (!res.ok) {
        throw new Error(`/me failed: ${res.status}`);
      }
      return (await res.json()) as MeResponse;
    },

    async logout() {
      const res = await fetch(`${base}/logout`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) {
        throw new Error(`/logout failed: ${res.status}`);
      }
    },

    async generateLineCode(input) {
      const res = await fetch(`${base}/line/link-code`, {
        method: "POST",
        credentials: "include",
        headers: { "X-App-Id": input.appId },
      });
      if (!res.ok) {
        throw new Error(`/line/link-code failed: ${res.status}`);
      }
      return (await res.json()) as GenerateLineCodeResult;
    },

    async unlinkLine(input) {
      const res = await fetch(`${base}/line/unlink`, {
        method: "POST",
        credentials: "include",
        headers: { "X-App-Id": input.appId },
      });
      if (!res.ok) {
        throw new Error(`/line/unlink failed: ${res.status}`);
      }
    },
  };
}
